import type { JSONSchemaNode } from '@/lib/api/types';
import {
  CLIENT_TYPES,
  TEAM_GROUPS,
  FIELD_NOTE_BLOCK_TYPES,
  INSIGHT_TOPICS,
  SERVICE_LAYERS,
  enumLabels,
  type VocabOption,
} from './vocab';

/**
 * Content-collection registry. One config-driven engine serves every collection;
 * adding a collection is a single entry here. `docSchema` is the console-shipped,
 * per-collection doc form definition — but it is only the **offline fallback**: the
 * backend serves the authoritative schema at `/admin/collection-schemas` and wins at
 * runtime (SchemaForm renders whatever JSON Schema it gets). Keep this in sync with
 * the Phase-1 docschema so the offline editor matches the live one.
 *
 * Y/DIRECTION (CMS plan D4) ships exactly two collections: `field-notes` (the
 * thought-leadership essays — the one true V1 collection) and `site-assets` (the
 * key-addressed media slots). The eight legacy collections are dropped; every other
 * page is page-copy (D1), authored in Settings → Page copy.
 */
export interface FacetDef {
  key: string;
  label: string;
  /**
   * Facet kind. Y/DIRECTION's V1 collections declare no facets, but the generic
   * engine (ContentListPage / SchemaForm) still supports the full set — keep them
   * so the offline filter UI typechecks against the authoritative backend schema.
   */
  type: 'text' | 'boolean' | 'enum' | 'ref-slug';
  /** Static options for `enum` facets. */
  options?: VocabOption[];
  /** Collection key for `ref-slug` facets. */
  refCollection?: string;
}

export interface CollectionConfig {
  /** route segment under /admin and the API path segment. */
  key: string;
  /** i18n nav key. */
  navKey: string;
  /** Display label (EN fallback for chrome). */
  label: string;
  /** Singular i18n label key — used for the "New {singular}" editor heading. */
  singularLabel: string;
  identType: 'slug' | 'key';
  hasSeo: boolean;
  /** When true, items can only be edited — no create or delete (a fixed register of records). */
  editOnly?: boolean;
  facets: FacetDef[];
  /** JSON-Schema for the bilingual `doc` form. */
  docSchema: JSONSchemaNode;
}

const I18N: JSONSchemaNode = { $ref: '#/$defs/I18n' };
const I18N_RICH: JSONSchemaNode = { $ref: '#/$defs/I18nRich' };

const DEFS = {
  I18n: { type: 'object', properties: { en: { type: 'string' }, ar: { type: 'string' } } },
  I18nRich: { type: 'object', properties: { en: { type: 'string' }, ar: { type: 'string' } } },
} as const;

export const SCHEMA_DEFS = DEFS as unknown as Record<string, JSONSchemaNode>;

const enumField = (
  options: VocabOption[],
  title: string,
  hint?: string,
): JSONSchemaNode => ({
  type: 'string',
  title,
  enum: options.map((o) => o.value),
  'x-enum-labels': enumLabels(options),
  ...(hint ? { 'x-hint': hint } : {}),
});

// ── Field-note building blocks (mirror of `fieldNoteDocSchema`) ───────────────
/**
 * Author byline shown on the article (name + role, bilingual).
 *
 * `is_studio` says the practice signed the piece rather than a named individual
 * — the website publishes an `Organization` instead of a `Person` in the
 * article's structured data. Explicit, because the alternative is guessing from
 * the name string and publishing the guess to search engines.
 */
const AUTHOR_BLOCK: JSONSchemaNode = {
  type: 'object',
  title: 'fields.author',
  properties: {
    name: { ...I18N, title: 'fields.authorName' },
    role: { ...I18N, title: 'fields.authorRole' },
    is_studio: {
      type: 'boolean',
      title: 'fields.authorIsStudio',
      'x-hint': 'fields.authorIsStudioHint',
    },
  },
};

/**
 * The hero image. Mirrors the backend `media` object: a path + bilingual alt +
 * intrinsic dimensions. The backend schema (authoritative) drives the live media
 * picker; this fallback exposes the same field names.
 */
const HERO_BLOCK: JSONSchemaNode = {
  type: 'object',
  title: 'fields.hero',
  properties: {
    src: { type: 'string', title: 'fields.heroSrc', 'x-hint': 'fields.heroSrcHint' },
    alt: { ...I18N, title: 'fields.altText' },
    width: { type: 'integer', title: 'fields.width' },
    height: { type: 'integer', title: 'fields.height' },
  },
};

/**
 * One block of article body — the permissive superset of the typed union
 * (paragraph | subheading | list | pullquote | aside | figure). Every member field
 * is optional, gated by `type`; only `type` is closed so an unknown block can never
 * reach a renderer with no case for it. Authored as two parallel locale arrays.
 */
const BODY_BLOCK: JSONSchemaNode = {
  type: 'object',
  title: 'fields.bodyBlock',
  properties: {
    type: enumField(FIELD_NOTE_BLOCK_TYPES, 'fields.blockType'),
    text: { type: 'string', title: 'fields.blockText', 'x-hint': 'fields.blockTextHint' },
    items: { type: 'array', title: 'fields.blockItems', items: { type: 'string' } },
    attribution: { type: 'string', title: 'fields.blockAttribution', 'x-hint': 'fields.blockAttributionHint' },
    caption: { type: 'string', title: 'fields.blockCaption' },
  },
};

/** A bilingual string list {en:[…], ar:[…]} — the i18nArr backend shape. */
const I18N_STRING_ARRAY: JSONSchemaNode = {
  type: 'object',
  properties: {
    en: { type: 'array', title: 'fields.bodyEn', items: { type: 'string' } },
    ar: { type: 'array', title: 'fields.bodyAr', items: { type: 'string' } },
  },
};

/** Bilingual list helper with a per-field title (mirrors the backend i18nArr). */
const i18nStringArray = (title: string, hint?: string): JSONSchemaNode => ({
  ...I18N_STRING_ARRAY,
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/** Localized body: two parallel block arrays {en:[block], ar:[block]} (like field-notes). */
const LOCALIZED_BODY: JSONSchemaNode = {
  type: 'object',
  title: 'fields.body',
  'x-hint': 'fields.bodyHint',
  properties: {
    en: { type: 'array', title: 'fields.bodyEn', items: BODY_BLOCK },
    ar: { type: 'array', title: 'fields.bodyAr', items: BODY_BLOCK },
  },
};

/** The article's closing call to action (its single next step → /start). */
const FINAL_CTA_LEAF: JSONSchemaNode = {
  type: 'object',
  properties: {
    heading: { type: 'string', title: 'fields.ctaHeading' },
    promise: { type: 'string', title: 'fields.ctaPromise' },
    primary: {
      type: 'object',
      title: 'fields.ctaPrimary',
      properties: {
        label: { type: 'string', title: 'fields.ctaLabel' },
        href: { type: 'string', title: 'fields.ctaHref' },
      },
    },
    secondary: {
      type: 'object',
      title: 'fields.ctaSecondary',
      properties: {
        label: { type: 'string', title: 'fields.ctaLabel' },
        href: { type: 'string', title: 'fields.ctaHref' },
      },
    },
    marginNote: { type: 'string', title: 'fields.ctaMarginNote' },
  },
};

export const COLLECTIONS: CollectionConfig[] = [
  {
    // The thought-leadership essays. A slug-addressed, SEO-bearing collection: the
    // list feeds the Field Notes index, by-slug feeds the article page, and the
    // slugs feed generateStaticParams + the sitemap. The display `title` is the
    // editor's dedicated field (mirrored into doc.title by the service), so it is
    // absent here — one title control, like every collection. No facets: a field
    // note's `category` is a localized label, not a queryable key (Phase-1 D4).
    key: 'field-notes',
    navKey: 'fieldNotes',
    label: 'collections.fieldNotes.label',
    singularLabel: 'collections.fieldNotes.singular',
    identType: 'slug',
    hasSeo: true,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.fieldNoteContent',
      $defs: SCHEMA_DEFS,
      properties: {
        // Index-card fields (what the Field Notes list renders).
        category: { ...I18N, title: 'fields.category', 'x-hint': 'fields.fieldNoteCategoryHint' },
        topic: enumField(INSIGHT_TOPICS, 'fields.topic', 'fields.topicHint'),
        excerpt: { ...I18N, title: 'fields.excerpt', 'x-hint': 'fields.excerptHint' },
        reading_time: { ...I18N, title: 'fields.readingTime', 'x-hint': 'fields.readingTimeHint' },
        date: { ...I18N, title: 'fields.displayDate', 'x-hint': 'fields.displayDateHint' },
        date_iso: { type: 'string', title: 'fields.dateIso', 'x-hint': 'fields.dateIsoHint' },
        featured: { type: 'boolean', title: 'fields.featured', 'x-hint': 'fields.fieldNoteFeaturedHint' },
        hero: HERO_BLOCK,
        // Article fields (what the reading page renders).
        question: { ...I18N, title: 'fields.question', 'x-hint': 'fields.questionHint' },
        arabic_line: { ...I18N, title: 'fields.arabicLine', 'x-hint': 'fields.arabicLineHint' },
        lede: { ...I18N_RICH, title: 'fields.lede', 'x-hint': 'fields.ledeHint' },
        author: AUTHOR_BLOCK,
        body: {
          type: 'object',
          title: 'fields.body',
          'x-hint': 'fields.bodyHint',
          properties: {
            en: { type: 'array', title: 'fields.bodyEn', items: BODY_BLOCK },
            ar: { type: 'array', title: 'fields.bodyAr', items: BODY_BLOCK },
          },
        },
        final_cta: {
          type: 'object',
          title: 'fields.finalCta',
          'x-hint': 'fields.finalCtaHint',
          properties: {
            en: { ...FINAL_CTA_LEAF, title: 'fields.finalCtaEn' },
            ar: { ...FINAL_CTA_LEAF, title: 'fields.finalCtaAr' },
          },
        },
      },
    },
  },
  {
    // The two-layer service offer (core ⇄ add-on). Slug-addressed, SEO-bearing:
    // the list feeds the services overview, by-slug feeds the detail page. Backend
    // schema entity: `service`. `title` is the editor's dedicated field.
    key: 'services',
    navKey: 'services',
    label: 'collections.services.label',
    singularLabel: 'collections.services.singular',
    identType: 'slug',
    hasSeo: true,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.serviceContent',
      $defs: SCHEMA_DEFS,
      properties: {
        layer: enumField(SERVICE_LAYERS, 'fields.layer', 'fields.layerHint'),
        scope: { ...I18N, title: 'fields.scope' },
        summary: { ...I18N, title: 'fields.summary' },
        order: { type: 'integer', title: 'fields.order' },
        hero: HERO_BLOCK,
        body: LOCALIZED_BODY,
      },
    },
  },
  {
    // The proof layer — one reusable case-study structure. Slug-addressed,
    // SEO-bearing. Backend schema entity: `case_study`. Results / testimonial ship
    // as marked placeholders until verified.
    key: 'case-studies',
    navKey: 'caseStudies',
    label: 'collections.caseStudies.label',
    singularLabel: 'collections.caseStudies.singular',
    identType: 'slug',
    hasSeo: true,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.caseStudyContent',
      $defs: SCHEMA_DEFS,
      properties: {
        client: { ...I18N, title: 'fields.client' },
        client_type: enumField(CLIENT_TYPES, 'fields.clientType'),
        type_label: { ...I18N, title: 'fields.typeLabel' },
        industry: { ...I18N, title: 'fields.industry' },
        context: { ...I18N, title: 'fields.context' },
        challenge: { ...I18N, title: 'fields.challenge' },
        solution: { ...I18N, title: 'fields.solution' },
        deliverables: i18nStringArray('fields.deliverables'),
        results: i18nStringArray('fields.results'),
        result_placeholder: { type: 'boolean', title: 'fields.resultPlaceholder' },
        hero: HERO_BLOCK,
        body: LOCALIZED_BODY,
        testimonial: {
          type: 'object',
          title: 'fields.testimonial',
          properties: {
            quote: { ...I18N, title: 'fields.quote' },
            name: { type: 'string', title: 'fields.authorName' },
            role: { type: 'string', title: 'fields.role' },
            company: { type: 'string', title: 'fields.company' },
            placeholder: { type: 'boolean', title: 'fields.placeholder' },
          },
        },
        final_cta: {
          type: 'object',
          title: 'fields.finalCta',
          'x-hint': 'fields.finalCtaHint',
          properties: {
            en: { ...FINAL_CTA_LEAF, title: 'fields.finalCtaEn' },
            ar: { ...FINAL_CTA_LEAF, title: 'fields.finalCtaAr' },
          },
        },
      },
    },
  },
  {
    // The pricing tiers. Key-addressed, no SEO. Backend schema entity: `package`.
    // `name` is the editor's dedicated title field.
    key: 'packages',
    navKey: 'packages',
    label: 'collections.packages.label',
    singularLabel: 'collections.packages.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.packageContent',
      $defs: SCHEMA_DEFS,
      properties: {
        ideal_for: { ...I18N, title: 'fields.idealFor' },
        inclusions: i18nStringArray('fields.inclusions'),
        investment: { ...I18N, title: 'fields.investment' },
        recommended: { type: 'boolean', title: 'fields.recommended' },
        cta: {
          type: 'object',
          title: 'fields.cta',
          properties: {
            label: { ...I18N, title: 'fields.ctaLabelField' },
            href: { type: 'string', title: 'fields.ctaHref' },
          },
        },
        order: { type: 'integer', title: 'fields.order' },
      },
    },
  },
  {
    // The client roster. Key-addressed, no SEO. Backend schema entity: `client`.
    // `name` is the editor's dedicated title field.
    key: 'clients',
    navKey: 'clients',
    label: 'collections.clients.label',
    singularLabel: 'collections.clients.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.clientContent',
      $defs: SCHEMA_DEFS,
      properties: {
        logo: HERO_BLOCK,
        industry: { ...I18N, title: 'fields.industry' },
        type: enumField(CLIENT_TYPES, 'fields.clientType'),
        type_label: { ...I18N, title: 'fields.typeLabel' },
        website: { type: 'string', title: 'fields.website' },
        case_study_slug: { type: 'string', title: 'fields.caseStudySlug' },
        order: { type: 'integer', title: 'fields.order' },
      },
    },
  },
  {
    // The one-pager's team band. Key-addressed, no SEO. Backend schema entity:
    // `team_member`. `name` is the editor's dedicated title field — a person's
    // name, or the craft on an unfilled seat.
    //
    // Two groups only (Our Founders / Our Team), per the client revision guide.
    //
    // Two INDEPENDENT flags, because "we do not invent names" produces two very
    // different honest states. `seat` marks an entry that names a craft rather
    // than a person: with no photograph uploaded the site renders it without a
    // portrait and shows `seat_note` instead. `unnamed` marks a real person
    // whose name is not published yet — the founders — which renders exactly
    // like a person, portrait included, and only withholds the name (and the
    // `Person` structured data that would otherwise claim "Founder" is a name).
    // Clearing either flag and typing a real name promotes the entry — no
    // developer involved. Both are explicit rather than "portrait is empty", so
    // a real colleague awaiting a photograph never renders as an empty chair.
    key: 'team-members',
    navKey: 'teamMembers',
    label: 'collections.teamMembers.label',
    singularLabel: 'collections.teamMembers.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.teamMemberContent',
      $defs: SCHEMA_DEFS,
      properties: {
        role: { ...I18N, title: 'fields.teamRole', 'x-hint': 'fields.teamRoleHint' },
        group: enumField(TEAM_GROUPS, 'fields.teamGroup', 'fields.teamGroupHint'),
        note: { ...I18N, title: 'fields.note', 'x-hint': 'fields.teamNoteHint' },
        seat: { type: 'boolean', title: 'fields.teamSeat', 'x-hint': 'fields.teamSeatHint' },
        seat_note: { ...I18N, title: 'fields.teamSeatNote', 'x-hint': 'fields.teamSeatNoteHint' },
        unnamed: { type: 'boolean', title: 'fields.teamUnnamed', 'x-hint': 'fields.teamUnnamedHint' },
        // A media REFERENCE, not an inline image object: `*_id` makes the console
        // render the asset picker, and the public read hydrates a resolved
        // `portrait_image` sibling. Portraits are the one image with no bundled
        // placeholder to override, so the picker is the only route onto the band.
        portrait_image_id: {
          type: 'string',
          title: 'fields.teamPhoto',
          'x-hint': 'fields.teamPhotoHint',
        },
        email: { type: 'string', title: 'fields.teamEmail' },
        linkedin: { type: 'string', title: 'fields.teamLinkedin' },
        order: { type: 'integer', title: 'fields.order' },
      },
    },
  },
  {
    // Client testimonials. Key-addressed, no SEO. Backend schema entity:
    // `testimonial`. The person's `name` is the editor's dedicated title field.
    key: 'testimonials',
    navKey: 'testimonials',
    label: 'collections.testimonials.label',
    singularLabel: 'collections.testimonials.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.testimonialContent',
      $defs: SCHEMA_DEFS,
      properties: {
        quote: { ...I18N, title: 'fields.quote' },
        role: { ...I18N, title: 'fields.role' },
        company: { ...I18N, title: 'fields.company' },
        logo: HERO_BLOCK,
        case_study_slug: { type: 'string', title: 'fields.caseStudySlug' },
        placeholder: { type: 'boolean', title: 'fields.placeholder' },
        order: { type: 'integer', title: 'fields.order' },
      },
    },
  },
  {
    // Site-wide image slots (logo, homepage figure, share image). A fixed register
    // of records — edit only — each holding one media reference the website reads
    // by `key`. Backend schema entity: `site_asset`.
    key: 'site-assets',
    navKey: 'siteAssets',
    label: 'collections.siteAssets.label',
    singularLabel: 'collections.siteAssets.singular',
    identType: 'key',
    hasSeo: false,
    editOnly: true,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.siteAssetContent',
      $defs: SCHEMA_DEFS,
      properties: {
        image_id: { type: 'string', format: 'uuid', title: 'fields.image', 'x-hint': 'fields.imageHint' },
        alt: { ...I18N, title: 'fields.altText' },
      },
    },
  },
];

export function getCollection(key: string): CollectionConfig | undefined {
  return COLLECTIONS.find((c) => c.key === key);
}
